/**
 * Utilitaires pour les sessions inter-entreprise
 * Fonctions pures <20 lignes chacune
 */

export function formatDateForInput(dateStr) {
  if (!dateStr) return ''
  const parts = dateStr.split('/')
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`
  }
  return dateStr
}

export function formatRelativeDate(dateStr) {
  if (!dateStr) return ''
  
  let date
  // Si c'est déjà un objet Date
  if (dateStr instanceof Date) {
    date = dateStr
  }
  // Si c'est une date ISO (format: YYYY-MM-DD ou YYYY-MM-DDTHH:mm:ss...)
  else if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    date = new Date(dateStr)
  } else if (typeof dateStr === 'string') {
    // Essayer le format DD/MM/YYYY
    const parts = dateStr.split('/')
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10) - 1
      const year = parseInt(parts[2], 10)
      date = new Date(year, month, day)
    } else {
      // Essayer de parser directement
      date = new Date(dateStr)
    }
  } else {
    date = new Date(dateStr)
  }
  
  if (isNaN(date.getTime())) return String(dateStr)
  
  const now = new Date()
  const diffTime = now - date
  const diffMs = Math.abs(diffTime)
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)
  
  // Pour les dates passées (mise à jour), afficher "il y a X"
  if (diffTime > 0) {
    if (diffDays === 0) {
      if (diffHours === 0) {
        if (diffMinutes === 0) return 'Il y a quelques secondes'
        if (diffMinutes === 1) return 'Il y a 1 minute'
        return `Il y a ${diffMinutes} minutes`
      }
      if (diffHours === 1) return 'Il y a 1 heure'
      return `Il y a ${diffHours} heures`
    }
    if (diffDays === 1) return 'Il y a 1 jour'
    return `Il y a ${diffDays} jours`
  }
  
  // Pour les dates futures (ne devrait pas arriver pour une mise à jour, mais on gère quand même)
  const futureDiffDays = Math.floor(-diffTime / (1000 * 60 * 60 * 24))
  if (futureDiffDays === 0) return 'Aujourd\'hui'
  if (futureDiffDays === 1) return 'Demain'
  return `Dans ${futureDiffDays} jours`
}

export function formatFullDate(dateStr) {
  if (!dateStr) return ''
  
  let date
  // Si c'est déjà un objet Date
  if (dateStr instanceof Date) {
    date = dateStr
  }
  // Si c'est une date ISO (format: YYYY-MM-DD ou YYYY-MM-DDTHH:mm:ss...)
  else if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    date = new Date(dateStr)
  } else if (typeof dateStr === 'string') {
    // Essayer le format DD/MM/YYYY
    const parts = dateStr.split('/')
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10) - 1
      const year = parseInt(parts[2], 10)
      date = new Date(year, month, day)
    } else {
      // Essayer de parser directement
      date = new Date(dateStr)
    }
  } else {
    date = new Date(dateStr)
  }
  
  if (isNaN(date.getTime())) return String(dateStr)
  
  return date.toLocaleDateString('fr-FR', { 
    weekday: 'long', 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Formate le lieu d'une session inter-entreprise
 * @param {Object} session - Session avec trainingAddress ou lieu
 * @returns {string} Lieu formaté
 */
export function formatSessionLieu(session) {
  const addr = session.trainingAddress || session.lieu
  if (!addr) return 'Lieu non précisé'
  return `${addr.ville || ''}${addr.codePostal ? ` (${addr.codePostal})` : ''}`.trim() || 'Lieu non précisé'
}

/**
 * Formate les dates d'une session inter-entreprise
 * @param {Object} session - Session avec datesFormation, dateDebut, dateFin
 * @returns {string} Dates formatées
 */
export function formatSessionDates(session) {
  const dates = session.datesFormation || []
  if (dates.length > 0) {
    return dates.map(d => new Date(d).toLocaleDateString('fr-FR')).join(', ')
  }
  if (session.dateDebut) {
    const debut = new Date(session.dateDebut).toLocaleDateString('fr-FR')
    const fin = session.dateFin ? new Date(session.dateFin).toLocaleDateString('fr-FR') : debut
    return `Du ${debut} au ${fin}`
  }
  return 'Dates à confirmer'
}

/**
 * Une session est complète quand il ne lui reste aucune place : zéro, ou moins
 * après une surréservation. Vide ou nul veut dire « places non renseignées ».
 * @param {number|string|null} disponibilite - Places restantes
 * @returns {boolean}
 */
export function estSessionComplete(disponibilite) {
  if (disponibilite === null || disponibilite === undefined || disponibilite === '') return false
  const places = Number(disponibilite)
  return Number.isFinite(places) && places <= 0
}

export function getAttributVariant(attribut) {
  if (!attribut) return 'default'
  const attrLower = attribut.toLowerCase()
  if (attrLower === 'initial') return 'success'
  if (attrLower === 'recyclage') return 'info'
  return 'default'
}
